import { useState } from "react";
import { Hourglass, AlertTriangle, CalendarPlus } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { F, btn } from "@/components/CaseBits";

const TONE = {
  running: "border-border bg-card",
  warning: "border-amber-400 bg-amber-50 dark:bg-amber-950/30",
  critical: "border-red-500 bg-red-50 dark:bg-red-950/30",
  expired: "border-red-700 bg-red-100 dark:bg-red-950/50",
};

/** Limitation tracker: 1y 3m from posting for claimant's evidence, extendable once by 6m. */
export const LimitationPanel = ({ kase, caseId, isArb, isAdv, invoices, onReload }) => {
  const lim = kase.limitation;
  const [start, setStart] = useState("");
  const [busy, setBusy] = useState(false);
  const pending = invoices.filter((i) => i.kind === "limitation_extension" && i.status !== "paid");

  const call = async (fn, ok) => {
    setBusy(true);
    try { await fn(); toast.success(ok); onReload(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  if (!lim) {
    return (
      <section className="border border-dashed border-border p-5 flex flex-wrap items-end justify-between gap-4" data-testid="limitation-panel">
        <div>
          <p className="flex items-center gap-2 label-caps"><Hourglass className="h-3.5 w-3.5" /> Limitation tracker</p>
          <p className="text-sm text-muted-foreground mt-2 max-w-xl">
            The period of 1 year 3 months starts when the matter is posted for the claimant's evidence.
            It starts automatically when such a sitting is scheduled{isArb ? ", or set the date here." : "."}
          </p>
        </div>
        {isArb && (
          <div className="flex items-end gap-3">
            <F label="Posted for claimant's evidence on" type="date" value={start} onChange={setStart} testId="limitation-start-input" />
            <Button disabled={!start || busy} className={btn} data-testid="limitation-start-button"
              onClick={() => call(() => api.post(`/cases/${caseId}/limitation/start`, { started_at: start }), "Limitation period started")}>
              Start
            </Button>
          </div>
        )}
      </section>
    );
  }

  return (
    <section className={`border p-5 ${TONE[lim.state]}`} data-testid="limitation-panel" data-state={lim.state}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="flex items-center gap-2 label-caps">
            {lim.state === "running" ? <Hourglass className="h-3.5 w-3.5" /> : <AlertTriangle className="h-3.5 w-3.5" />}
            Limitation tracker
          </p>
          <p className="font-display text-2xl mt-2" data-testid="limitation-deadline">
            {lim.state === "expired" ? "Period expired on " : "Award due by "}{fmtDate(lim.deadline)}
          </p>
          <p className="text-sm mt-1" data-testid="limitation-days-left">
            {lim.days_left < 0 ? `${-lim.days_left} day(s) past the deadline` : `${lim.days_left} day(s) remaining`}
            {" · "}posted for claimant's evidence on {fmtDate(lim.started_at)}
            {lim.extensions?.length > 0 && ` · extended ${lim.extensions.length}× by 6 months`}
          </p>
        </div>
        <div className="text-right text-xs text-muted-foreground max-w-xs">
          Alerts go to the tribunal and counsel 30 and 7 days before the deadline.
          {lim.alerts_sent?.length > 0 && <span className="block mt-1">Alerts issued: {lim.alerts_sent.join(", ")} days</span>}
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-4">
        {pending.length > 0 ? (
          <p className="text-sm" data-testid="limitation-extension-pending">
            Extension of 6 months requested — awaiting payment of INR 500 from{" "}
            {pending.map((p) => p.side).join(" and ")}. Settle the fee note{pending.length > 1 ? "s" : ""} on the Fees tab.
          </p>
        ) : (isArb || isAdv) && (
          <Button variant="outline" disabled={busy} data-testid="limitation-extend-button"
            className="rounded-none uppercase tracking-widest text-xs h-9"
            onClick={() => call(() => api.post(`/cases/${caseId}/limitation/extend`), "Extension fee notes raised (INR 500 per side)")}>
            <CalendarPlus className="h-3.5 w-3.5 mr-2" /> Seek 6-month extension · INR 500 per side
          </Button>
        )}
      </div>
    </section>
  );
};
